import { AppState } from '../reducers';

// services
import * as Services from '../../services/ProviderInbox.Services';

// models
import {
  ProviderInboxIdentifyResult,
  ProviderInboxResolveRequest,
  ProviderInboxResolveResult,
} from '../../models/Entities/ProviderInbox/ProviderInbox';
import { ErrorType } from '../../models/HttpError';
import { toErrorType } from './ProviderConsumptionImport.Middleware';

/**
 * Bandeja de proveedores (spec 009, US4): alta de proveedor desde la factura. El token se lee sólo acá; los thunks
 * devuelven promesas (sin estado en el store): el drawer "Alta desde factura" guarda cada fila en su estado local.
 * Rechazan con ErrorType (el 409 "ya resuelta" conserva `AlreadyResolved`).
 */

export type ProviderInboxThunk<TResult> = (dispatch: (action: unknown) => void, getState: () => AppState) => Promise<TResult>;

const NotAuthorized: ErrorType = { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] };

const run = async <T>(getState: () => AppState, call: (token: string) => Promise<T>): Promise<T> => {
  const token = getState().oidc?.user?.access_token;
  if (!token) throw NotAuthorized;
  try {
    return await call(token);
  } catch (error: unknown) {
    throw toErrorType(error);
  }
};

export const actionCreators = {
  /** Lee la factura (multipart) e identifica al emisor; devuelve la acción propuesta. */
  IdentifyProviderInvoice:
    (file: File): ProviderInboxThunk<ProviderInboxIdentifyResult> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.IdentifyProviderInvoice(token, file)),

  /** Asocia el archivo a un proveedor existente o crea uno (`create { businessName, taxId? }`). */
  ResolveProviderInbox:
    (inboxId: string, body: ProviderInboxResolveRequest): ProviderInboxThunk<ProviderInboxResolveResult> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.ResolveProviderInbox(token, inboxId, body)),

  /** Descarta un archivo sin resolver (idempotente). */
  DiscardProviderInbox:
    (inboxId: string): ProviderInboxThunk<void> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.DiscardProviderInbox(token, inboxId)),
};
